'use strict';

/**
 * T-MODULE-MASTER-MINTFALLBACK-HAZARD (GOTCHA-575 defect (a)) — mint guard.
 *
 * WHY THIS EXISTS
 * ---------------
 * The Proven-Path fallback in module-client.js used to mint a fresh random
 * 32-byte module master whenever `session.moduleMasters[moduleId]` was absent.
 * sessionStorage is NOT durable — it clears on tab close, storage pressure, or
 * a privacy sweep. If it cleared between queued key writes, the envelope that
 * survived on the server wrapped a DIFFERENT master than the data was encrypted
 * under, and the user's data became permanently unreadable. It surfaced as a
 * blank module: no error, no signal.
 *
 * Per the Legal Foundation (CLAUDE.md): HGE is a custodian of CIPHERTEXT, not of
 * data. There is no server-side copy of the master to fall back on — a lost
 * master is unrecoverable BY DESIGN. That makes silently minting a replacement
 * the single worst available failure mode, strictly worse than refusing.
 *
 * THE RULE: never mint a replacement master for an account that already has one.
 * Fail CLOSED and ask for re-derivation instead. Minting stays legal ONLY for a
 * genuinely first-time module — and that has to be PROVEN, not assumed.
 *
 * THE DURABLE SIGNAL
 * ------------------
 * The decision must not be inferred from the very sessionStorage that just
 * cleared. It comes from the SERVER, which is the only store that survives:
 *
 *   probeKeyEnvelope  → GET /auth/vault/me/key/:moduleId   (404 ⇒ absent)
 *   probeDataBlob     → GET /auth/vault/me/blob/:moduleId  (404 ⇒ absent)
 *
 * Either artifact existing proves keys/data already live under a PRIOR master.
 * Both definitively absent is the only state that proves genuine first use.
 *
 * Probes are dependency-injected (Node-testable, browser-reusable) and return:
 *   true  — artifact exists
 *   false — artifact definitively absent (a clean 404)
 *   throw / anything else — INDETERMINATE (network error, 429 throttle, 500)
 *
 * An indeterminate probe is NOT permission to mint. Ambiguity fails closed.
 */

const MINT_VERDICTS = Object.freeze({
  ALLOW: 'ALLOW',
  REFUSE_EXISTING_MASTER: 'REFUSE_EXISTING_MASTER',
  REFUSE_INDETERMINATE: 'REFUSE_INDETERMINATE',
});

const UNKNOWN = Symbol('unknown');

async function _runProbe(fn) {
  if (typeof fn !== 'function') return UNKNOWN;
  try {
    const r = await fn();
    if (r === true) return true;
    if (r === false) return false;
    return UNKNOWN;           // null/undefined/anything else is not an answer
  } catch (_e) {
    return UNKNOWN;           // network error, 429, 500 — we simply do not know
  }
}

/**
 * Decide whether minting a fresh module master is safe.
 *
 * @param {string} moduleId
 * @param {object} deps
 * @param {Function} [deps.probeKeyEnvelope] async () => true|false
 * @param {Function} [deps.probeDataBlob]    async () => true|false
 * @returns {Promise<{verdict:string, allowMint:boolean, needsReDerivation:boolean,
 *                    moduleId:string, reason:string}>}
 */
async function assessMint(moduleId, deps) {
  const d = deps || {};
  const [hasKey, hasBlob] = await Promise.all([
    _runProbe(d.probeKeyEnvelope),
    _runProbe(d.probeDataBlob),
  ]);

  // A definitive "exists" on EITHER artifact ⇒ a prior master is in play.
  // Checked first so the reason code reports the real, actionable cause even
  // when the other probe also failed.
  if (hasKey === true || hasBlob === true) {
    return {
      verdict: MINT_VERDICTS.REFUSE_EXISTING_MASTER,
      allowMint: false,
      needsReDerivation: true,
      moduleId,
      reason: hasKey === true
        ? 'A key envelope already exists on the server for this module — its master must be re-derived, never replaced.'
        : 'Encrypted data already exists on the server for this module — its master must be re-derived, never replaced.',
    };
  }

  // Only two clean 404s prove genuine first use.
  if (hasKey === false && hasBlob === false) {
    return {
      verdict: MINT_VERDICTS.ALLOW,
      allowMint: true,
      needsReDerivation: false,
      moduleId,
      reason: 'No key envelope and no data blob on the server — genuine first use.',
    };
  }

  return {
    verdict: MINT_VERDICTS.REFUSE_INDETERMINATE,
    allowMint: false,
    needsReDerivation: true,
    moduleId,
    reason: 'Could not prove this module is new (a durable-state probe was inconclusive). '
      + 'Refusing to mint — minting over an existing master would destroy the data irrecoverably.',
  };
}

module.exports = { assessMint, MINT_VERDICTS };
